import React, {useCallback, useRef} from "react";
import {Editor} from "@tinymce/tinymce-react";
import {useDrop} from "react-dnd";

// Self-hosted TinyMCE 6 imports (bundled by webpack, no API key needed)
import 'tinymce/tinymce';
import 'tinymce/models/dom';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/table';
import 'tinymce/plugins/code';
import 'tinymce/plugins/image';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/help';
import 'tinymce/plugins/help/js/i18n/keynav/en';

interface EmailEditorProps {
    value: string;
    typeToAccept: string;
    onChangeHtml: (html: string) => void;
}

const hbsBadgeStyles = `
.hbs-block {
    display: inline-block;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    font-family: monospace;
    margin: 2px;
    vertical-align: middle;
    user-select: none;
}
.hbs-if, .hbs-each { background: #e3f2fd; color: #1565c0; border: 1px solid #90caf9; }
.hbs-endif, .hbs-endeach { background: #fce4ec; color: #c62828; border: 1px solid #ef9a9a; }
tr.hbs-block-row td { padding: 2px 4px !important; border: none !important; background: transparent !important; }
`;

export const QuillTextEditor: React.FC<EmailEditorProps> = ({value, onChangeHtml, typeToAccept}) => {
    const editorRef = useRef<any>(null);

    const [{canDrop}, dropRef] = useDrop(
        () => ({
            accept: typeToAccept,
            drop: (item: {fieldName: string}) => {
                const editor = editorRef.current;
                if (!editor) return;
                editor.focus();
                editor.insertContent(`{{${item.fieldName}}}`);
            },
            collect: (monitor) => ({
                canDrop: monitor.canDrop() && monitor.getItemType() !== null,
            }),
        }),
        []
    );

    const handleChange = useCallback((content: string) => {
        onChangeHtml(content);
    }, [onChangeHtml]);

    return (
        <div style={{position: 'relative'}}>
            {canDrop && (
                <div
                    ref={el => { dropRef(el); }}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 10,
                        background: 'rgba(13, 110, 253, 0.08)',
                        border: '2px dashed #0d6efd',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'auto',
                    }}
                >
                    <span style={{color: '#0d6efd', fontWeight: 600, fontSize: 14}}>
                        Drop field here
                    </span>
                </div>
            )}
            <Editor
                onInit={(_evt, editor) => {
                    editorRef.current = editor;
                }}
                value={value}
                onEditorChange={handleChange}
                init={{
                    height: 500,
                    menubar: false,
                    plugins: [
                        'lists', 'link', 'table', 'code', 'image', 'autolink',
                        'charmap', 'emoticons', 'fullscreen', 'preview',
                        'searchreplace', 'visualblocks', 'wordcount', 'insertdatetime',
                        'anchor', 'help'
                    ],
                    toolbar:
                        'undo redo | blocks fontfamily fontsize | ' +
                        'bold italic underline strikethrough | forecolor backcolor | ' +
                        'alignleft aligncenter alignright alignjustify | ' +
                        'bullist numlist outdent indent | ' +
                        'link image table charmap emoticons | ' +
                        'searchreplace | fullscreen preview code | removeformat | help',
                    valid_elements: '*[*]',
                    valid_children: '+body[style|link],+head[style|link]',
                    extended_valid_elements: 'span[*],div[*],table[*],tr[*],td[*],th[*],style[*],link[*]',
                    verify_html: false,
                    content_style: `
                        body { font-family: Arial, sans-serif; font-size: 16px; }
                        ${hbsBadgeStyles}
                    `,
                    noneditable_class: 'hbs-block',
                    placeholder: 'Type your message here…',
                    skin: false,
                    branding: false,
                    promotion: false,
                }}
            />
        </div>
    );
};
